import React, { useState, useEffect } from 'react';
import { 
  Handshake, Save, Loader2, AlertCircle, Clock, RefreshCw, 
  Users, ArrowLeftRight, Bell, Calendar, CheckCircle2 
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NegotiationConfigPanel = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/system-config/`, {
        headers: {
          'Authorization': 'Bearer cookie-auth',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Erreur lors du chargement');
      }

      const data = await response.json();
      setConfig(data.negotiation || {
        default_enabled: true,
        default_max_iterations: 2,
        default_quote_validity_days: 30,
        grace_period_days: 3,
        auto_expire_enabled: true,
        notify_before_expiry_days: 3,
        allow_client_initiate: true,
        allow_baobab_initiate: true
      });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/system-config/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer cookie-auth'
        },
        body: JSON.stringify({
          section: 'negotiation',
          data: config
        })
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la sauvegarde');
      }

      toast({
        title: 'Succès',
        description: 'Configuration de négociation enregistrée',
        variant: 'default'
      });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (!config) {
    return (
      <div className="text-center py-12 text-gray-500">
        Erreur de chargement de la configuration
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
        <Handshake className="w-5 h-5 text-amber-600 mt-0.5" />
        <div>
          <h4 className="font-semibold text-amber-900">Configuration de la Négociation</h4>
          <p className="text-sm text-amber-700 mt-1">
            Définissez les paramètres par défaut pour la boucle de négociation des devis.
            Ces paramètres s&apos;appliqueront aux nouvelles opérations.
          </p>
        </div>
      </div>

      {/* Main Toggle */}
      <div className="border rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-medium text-gray-900 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-600" />
              Négociation activée par défaut
            </h4>
            <p className="text-sm text-amber-700 mt-1">
              Les nouvelles opérations auront la négociation activée automatiquement
            </p>
          </div>
          <input
            type="checkbox"
            className="w-5 h-5 text-amber-600"
            checked={config.default_enabled}
            onChange={(e) => setConfig({...config, default_enabled: e.target.checked})}
          />
        </div>
      </div>

      {/* Iteration Settings */}
      <div className="border rounded-lg p-4 space-y-4">
        <h4 className="font-medium text-gray-900 flex items-center gap-2">
          <ArrowLeftRight className="w-4 h-4 text-blue-600" />
          Paramètres de la boucle de négociation
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nombre max d&apos;itérations (allers-retours)
            </label>
            <input
              type="number"
              min="1"
              max="10"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
              value={config.default_max_iterations}
              onChange={(e) => setConfig({...config, default_max_iterations: parseInt(e.target.value) || 2})}
            />
            <p className="text-xs text-gray-500 mt-1">
              Après ce nombre, la négociation est escaladée ou clôturée
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Validité du devis (jours)
            </label>
            <input
              type="number"
              min="1"
              max="90"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
              value={config.default_quote_validity_days}
              onChange={(e) => setConfig({...config, default_quote_validity_days: parseInt(e.target.value) || 30})}
            />
            <p className="text-xs text-gray-500 mt-1">
              Durée pendant laquelle le devis reste valide
            </p>
          </div>
        </div>
      </div>

      {/* Expiration Settings */}
      <div className="border rounded-lg p-4 space-y-4">
        <h4 className="font-medium text-gray-900 flex items-center gap-2">
          <Clock className="w-4 h-4 text-orange-600" />
          Gestion de l&apos;expiration
        </h4>
        
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div>
            <span className="font-medium text-gray-900">Expiration automatique</span>
            <p className="text-sm text-gray-500">Les devis expirent automatiquement après la période de validité</p>
          </div>
          <input
            type="checkbox"
            className="w-5 h-5 text-amber-600"
            checked={config.auto_expire_enabled}
            onChange={(e) => setConfig({...config, auto_expire_enabled: e.target.checked})}
          />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Délai de grâce (jours)
            </label>
            <input
              type="number"
              min="0"
              max="14"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
              value={config.grace_period_days}
              onChange={(e) => setConfig({...config, grace_period_days: parseInt(e.target.value) || 0})}
            />
            <p className="text-xs text-gray-500 mt-1">
              Période supplémentaire après expiration avant clôture définitive
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notification avant expiration (jours)
            </label>
            <input
              type="number"
              min="1"
              max="14"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
              value={config.notify_before_expiry_days}
              onChange={(e) => setConfig({...config, notify_before_expiry_days: parseInt(e.target.value) || 3})}
            />
            <p className="text-xs text-gray-500 mt-1">
              Envoyer un rappel X jours avant l&apos;expiration du devis
            </p>
          </div>
        </div>
      </div>

      {/* Permission Settings */}
      <div className="border rounded-lg p-4 space-y-4">
        <h4 className="font-medium text-gray-900 flex items-center gap-2">
          <Users className="w-4 h-4 text-purple-600" />
          Permissions de négociation
        </h4>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <span className="font-medium text-gray-900">Client peut initier une négociation</span>
              <p className="text-sm text-gray-500">Le client peut demander une réduction ou modifier le devis</p>
            </div>
            <input
              type="checkbox"
              className="w-5 h-5 text-amber-600"
              checked={config.allow_client_initiate}
              onChange={(e) => setConfig({...config, allow_client_initiate: e.target.checked})}
            />
          </div>
          
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <span className="font-medium text-gray-900">Baobab peut faire une contre-offre</span>
              <p className="text-sm text-gray-500">Les opérateurs peuvent proposer des modifications au devis initial</p>
            </div>
            <input
              type="checkbox"
              className="w-5 h-5 text-amber-600"
              checked={config.allow_baobab_initiate}
              onChange={(e) => setConfig({...config, allow_baobab_initiate: e.target.checked})}
            />
          </div>
        </div>
      </div>

      {/* Info Alert */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
        <div>
          <h4 className="font-semibold text-blue-900">Information</h4>
          <p className="text-sm text-blue-700 mt-1">
            Ces paramètres s&apos;appliquent uniquement aux <strong>nouvelles opérations</strong>. 
            Les opérations existantes conservent leurs paramètres actuels. 
            Vous pouvez modifier individuellement les paramètres de négociation pour chaque opération.
          </p>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex gap-4 pt-4">
        <button
          onClick={saveConfig}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
        >
          {saving ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Save className="w-5 h-5" />
          )}
          Enregistrer
        </button>
        
        <button
          onClick={fetchConfig}
          disabled={loading}
          className="flex items-center gap-2 px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className="w-5 h-5" />
          Réinitialiser
        </button>
      </div>
    </div>
  );
};

export default NegotiationConfigPanel;
